import React, { useEffect, useState, useCallback, useRef, useImperativeHandle } from "react";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { EmployeeDetails } from "../../components/ui/cards";
import { EMPLOYEE_ATTENDANCE_DAILY } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import dayjs from "dayjs";
import ToastMsg from "../../components/common/ToastMsg";
import { Calendar, Clock, User, Building, MapPin, CircleQuestionMark } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { Checkbox, Select, DatePicker, TimePicker } from "antd";
import { cn } from "../../utils/tailwindUtils";

// Attendance status options
const attendanceStatus = [
    { value: "present", text: "Present" },
    { value: "absent", text: "Absent" },
    { value: "on-duty", text: "On Duty" },
    { value: "holiday", text: "Holiday" },
    { value: "week-off", text: "Week Off" },
    { value: "WFH", text: "Work From Home" },
    { value: "leave", text: "Leave" },
    { value: "compensation", text: "Compensation" },
];

// Filter option for Select
const filterOption = (input, option) =>
    option.children.toLowerCase().includes(input.toLowerCase());

const AttendanceEdit = React.forwardRef(({ editDetails, onSuccess }, ref) => {
    const { update } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isApplyShift, setIsApplyShift] = useState(false);
    const [errors, setErrors] = useState({});
    const hideCursorRef = useRef();

    // Form state
    const [formData, setFormData] = useState({
        id: null,
        date: "",
        employeeDetails: {},
        shift: "",
        department: "",
        location: "",
        status: "",
        check_in: "",
        check_out: "",
        over_time: "00:00:00",
        break_hrs: "00:00:00",
        working_hrs: "00:00:00",
        description: "",
        shift_working_hours: "",
    });

    // Format time span helper function
    const formatTimeSpan = (input) => {
        if (!input || input === "NaT") return "00:00:00";

        if (typeof input === "string") {
            if (input.includes("days") || input.includes("day")) {
                const [daysPart, timePart] = input.split(", ");
                const [daysStr] = daysPart.split(" ");
                const days = parseInt(daysStr, 10) || 0;
                const [hours, minutes, seconds] = timePart.split(/[:,]/).map(Number);
                const totalHours = days * 24 + (hours || 0);
                return `${String(totalHours).padStart(2, "0")}:${String(minutes || 0).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
            }
            return input;
        }
        return "00:00:00";
    };

    // Initialize form data when editDetails changes
    useEffect(() => {
        if (editDetails) {
            const { employee_details } = editDetails;

            const empDetails = {
                title: employee_details?.title || "",
                first_name: employee_details?.employee_name || "",
                last_name: "",
                designation_id: {
                    id: employee_details?.designation?.id || "",
                    designation_name: employee_details?.designation?.name || ""
                },
                id: employee_details?.employee_id,
                profile: employee_details?.profile,
            };

            const formattedData = {
                id: editDetails?.attendance_id || null,
                date: editDetails?.date ? dayjs(editDetails.date).format("DD/MM/YYYY") : "",
                employeeDetails: empDetails,
                shift: editDetails?.shift || "",
                department: employee_details?.department?.name || "",
                location: editDetails?.location || "",
                status: editDetails?.status || "",
                check_in: (editDetails?.check_in && editDetails.check_in !== "NaT")
                    ? dayjs(editDetails.check_in).format("YYYY-MM-DDTHH:mm:ss")
                    : "",
                check_out: (editDetails?.check_out && editDetails.check_out !== "NaT")
                    ? dayjs(editDetails.check_out).format("YYYY-MM-DDTHH:mm:ss")
                    : "",
                over_time: (editDetails?.overtime && editDetails.overtime !== "NaT")
                    ? formatTimeSpan(editDetails.overtime)
                    : "00:00:00",
                break_hrs: (editDetails?.break_hrs && editDetails.break_hrs !== "NaT")
                    ? dayjs(editDetails.break_hrs, "H:mm:ss").format("HH:mm:ss")
                    : "00:00:00",
                working_hrs: (editDetails?.total_working_hours && editDetails.total_working_hours !== "NaT")
                    ? formatTimeSpan(editDetails.total_working_hours)
                    : "00:00:00",
                description: editDetails?.description || "",
                shift_working_hours: (editDetails?.shift_working_hours && editDetails.shift_working_hours !== "NaT")
                    ? dayjs(editDetails.shift_working_hours, "H:mm:ss").format("HH:mm:ss")
                    : "00:00:00",
            };

            setFormData(formattedData);
            setIsEditMode(true);
        }
    }, [editDetails]);

    // Prepare employee details for display
    const empDetails = React.useMemo(() => {
        if (!editDetails?.employee_details) return {};

        return {
            ...editDetails.employee_details,
            designation_id: editDetails.employee_details?.designation
                ? {
                    id: editDetails.employee_details.designation.id,
                    designation_name: editDetails.employee_details.designation.name,
                }
                : { id: 0, designation_name: "" },
            first_name: editDetails.employee_details?.employee_name || "-",
            id: editDetails.employee_details?.employee_id || "-",
        };
    }, [editDetails]);

    // Helper functions for time calculations
    const timeToSeconds = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes, seconds] = timeStr.split(":").map(Number);
        return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
    };

    const secondsToTime = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    // Calculate working hours
    const calculateWorkingHours = useCallback(() => {
        if (!formData.check_in || !formData.check_out) {
            setFormData(prev => ({
                ...prev,
                working_hrs: "00:00:00",
                over_time: "00:00:00"
            }));
            return;
        }

        try {
            const checkIn = dayjs(formData.check_in);
            let checkOut = dayjs(formData.check_out);

            // Handle night shifts (check-out next day)
            if (checkOut.isBefore(checkIn)) {
                checkOut = checkOut.add(1, "day");
            }

            const totalSeconds = checkOut.diff(checkIn, "seconds");
            const breakSeconds = timeToSeconds(formData.break_hrs);
            const netSeconds = Math.max(totalSeconds - breakSeconds, 0);

            const shiftSeconds = timeToSeconds(formData.shift_working_hours);
            const overtimeSeconds = Math.max(netSeconds - shiftSeconds, 0);

            setFormData(prev => ({
                ...prev,
                working_hrs: secondsToTime(netSeconds),
                over_time: secondsToTime(overtimeSeconds)
            }));
        } catch (error) {
            console.error("Error calculating working hours:", error);
        }
    }, [formData.check_in, formData.check_out, formData.break_hrs, formData.shift_working_hours]);

    // Recalculate when dependencies change
    useEffect(() => {
        calculateWorkingHours();
    }, [calculateWorkingHours]);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear errors when field is filled
        if (value && errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    // Handle date time changes for check in/out
    const handleApplyShiftToggle = (e) => {
        const isChecked = e.target.checked;
        setIsApplyShift(isChecked);

        if (isChecked) {
            const shift = editDetails?.shiftDetails;

            const dateOnly = formData?.date
                ? dayjs(formData.date, "DD/MM/YYYY").isValid()
                    ? dayjs(formData.date, "DD/MM/YYYY")
                    : null
                : null;

            if (dateOnly && shift?.shift_timings_from && shift?.shift_timings_to) {
                const shiftFrom = shift.shift_timings_from; // e.g. "22:00:00"
                const shiftTo = shift.shift_timings_to;     // e.g. "06:00:00"
                const isNightShift = shiftFrom > shiftTo;   // Simple string comparison works here

                const checkInDateTime = dayjs(`${dateOnly.format("YYYY-MM-DD")}T${shiftFrom}`);
                let checkOutDateTime = dayjs(`${dateOnly.format("YYYY-MM-DD")}T${shiftTo}`);

                if (isNightShift) {
                    checkOutDateTime = checkOutDateTime.add(1, 'day');  // Move to next day
                    ToastMsg("warning", "Applied night shift timings across dates.");
                }

                setFormData((prev) => ({
                    ...prev,
                    status: "present",
                    check_in: checkInDateTime.format("YYYY-MM-DDTHH:mm:ss"),
                    check_out: checkOutDateTime.format("YYYY-MM-DDTHH:mm:ss"),
                    description: `Applied shift timings`,
                }));
            }
        } else {
            // Optionally clear check-in/out when unchecked
            setFormData((prev) => ({
                ...prev,
                check_in: null,
                check_out: null,
            }));
        }
    };

    // Update the handleDateTimeChange function to handle dayjs objects properly
    const handleDateTimeChange = (field, value) => {
        if (value && value.isValid()) {
            const dateTimeValue = value.format("YYYY-MM-DDTHH:mm:ss");
            handleFieldChange(field, dateTimeValue);
        } else {
            handleFieldChange(field, "");
        }
    };

    // Handle time changes for break hours and shift hours
    const handleTimeChange = (field, value) => {
        const timeValue = value ? value.format("HH:mm:ss") : "00:00:00";
        handleFieldChange(field, timeValue);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.status) {
            newErrors.status = "Status is required";
        }

        if (formData.status === "present" || formData.status === "on-duty") {
            if (!formData.check_in) {
                newErrors.check_in = "Check-in time is required";
            }
            if (!formData.check_out) {
                newErrors.check_out = "Check-out time is required";
            }
            if (formData.check_in && formData.check_out) {
                const checkIn = dayjs(formData.check_in);
                const checkOut = dayjs(formData.check_out);
                if (checkOut.isBefore(checkIn)) {
                    newErrors.check_out = "Check-out time cannot be before check-in time";
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    useImperativeHandle(ref, () => ({
        handleSubmit: async () => {
            if (!validateForm()) {
                ToastMsg("error", "Please fix the errors before submitting");
                throw new Error("Validation failed");
            }

            try {
                setLoading(true);
                const payload = {
                    id: formData.id,
                    status: formData.status,
                    check_in: formData.check_in
                        ? dayjs(formData.check_in).format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    check_out: formData.check_out
                        ? dayjs(formData.check_out).format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    working_hrs: formData.working_hrs || "00:00:00",
                    over_time: formData.over_time || "00:00:00",
                    break_hrs: formData.break_hrs || "00:00:00",
                    description: formData.description || "",
                };

                const response = await update(EMPLOYEE_ATTENDANCE_DAILY, payload, false);

                if (response?.status === "success") {
                    ToastMsg("success", response.message || "Attendance updated successfully");
                    onSuccess?.();
                    return response;
                } else {
                    ToastMsg("error", response?.message || "Failed to update attendance");
                    throw new Error(response?.message || "Failed to update attendance");
                }
            } catch (error) {
                console.error("Update error:", error);
                ToastMsg("error", error.message || "Failed to update attendance");
                throw error;
            } finally {
                setLoading(false);
            }
        }
    }));


    // Check if time fields are required
    const isTimeRequired = formData.status === "present" || formData.status === "on-duty";

    return (
        <div className="h-[calc(100vh-300px)] flex flex-col">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-2">

                    {/* LEFT COLUMN */}
                    <div className="space-y-6">
                        {/* Employee & Work Info */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                            <div className="grid grid-cols-1 gap-6">

                                {/* Employee Info */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                                        <User className="w-4 h-4 text-sidebar dark:text-blue-400" />
                                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                                            Employee Information
                                        </h3>
                                    </div>
                                    <div className=" origin-left">
                                        <EmployeeDetails details={empDetails} size="xs" />
                                    </div>
                                </div>

                                {/* Work Details */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                                        <Building className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                                            Work Details
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                        {/* Shift */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                                Shift
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <CircleQuestionMark className="w-3 h-3 cursor-pointer text-gray-400 hover:text-gray-600" />
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="text-xs max-w-xs">
                                                        {editDetails?.shiftDetails ? (
                                                            <div className="space-y-1">
                                                                <p><strong>Timings:</strong> {editDetails.shiftDetails.shift_timings_from} - {editDetails.shiftDetails.shift_timings_to}</p>
                                                                <p><strong>Working Hours:</strong> {formData.shift_working_hours}</p>
                                                            </div>
                                                        ) : (
                                                            <p>No shift details available</p>
                                                        )}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium">{formData.shift || "-"}</p>
                                        </div>

                                        {/* Department */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Department</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium">{formData.department || "-"}</p>
                                        </div>

                                        {/* Location */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Location</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                {formData.location || "-"}
                                            </p>
                                        </div>

                                        {/* Date */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Date</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium flex items-center gap-1">
                                                <Calendar className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                {formData.date}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Time Details */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-2 mb-3">
                                <Clock className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Time Details</h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Check-In */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Check-In {isTimeRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    <DatePicker
                                        showTime
                                        format="DD-MM-YYYY hh:mm A"
                                        value={formData.check_in ? dayjs(formData.check_in) : null}
                                        onChange={(value) => handleDateTimeChange("check_in", value)}
                                        disabled={!isEditMode || !isTimeRequired}
                                        className={cn("w-full", errors.check_in ? "border-red-500" : "")}
                                        size="small"
                                    />
                                    {errors.check_in && (
                                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.check_in}</p>
                                    )}
                                </div>

                                {/* Check-Out */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Check-Out {isTimeRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    <DatePicker
                                        showTime
                                        format="DD-MM-YYYY hh:mm A"
                                        value={formData.check_out ? dayjs(formData.check_out) : null}
                                        onChange={(value) => handleDateTimeChange("check_out", value)}
                                        disabled={!isEditMode || !isTimeRequired}
                                        className={cn("w-full", errors.check_out ? "border-red-500" : "")}
                                        size="small"
                                    />
                                    {errors.check_out && (
                                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.check_out}</p>
                                    )}
                                </div>

                                {/* Break Hours */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Break Hours</label>
                                    <TimePicker
                                        format="HH:mm:ss"
                                        value={formData.break_hrs ? dayjs(formData.break_hrs, "HH:mm:ss") : null}
                                        onChange={(value) => handleTimeChange("break_hrs", value)}
                                        disabled={!isEditMode}
                                        className="w-full"
                                        size="small"
                                    />
                                </div>

                                {/* Shift Working Hours */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Shift Hours</label>
                                    <TimePicker
                                        format="HH:mm:ss"
                                        value={formData.shift_working_hours ? dayjs(formData.shift_working_hours, "HH:mm:ss") : null}
                                        onChange={(value) => handleTimeChange("shift_working_hours", value)}
                                        disabled={!isEditMode}
                                        className="w-full"
                                        size="small"
                                    />
                                </div>
                            </div>

                            {/* Calculated Hours */}
                            <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex flex-col gap-1 text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                    <label className="text-xs font-medium text-blue-700 dark:text-blue-300">Working Hours</label>
                                    <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">{formData.working_hrs}</p>
                                </div>
                                <div className="flex flex-col gap-1 text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                                    <label className="text-xs font-medium text-green-700 dark:text-green-300">Overtime</label>
                                    <p className="text-xs font-semibold text-green-900 dark:text-green-100">{formData.over_time}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="space-y-6">
                        {/* Attendance Status */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-2 mb-3">
                                <Calendar className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Attendance Status</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    showSearch
                                    size="Large"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        errors.status ? "border-red-500" : "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                    value={formData.status || ""}
                                    onChange={(value) => {
                                        const updatedFormData = {
                                            ...formData,
                                            status: value,
                                            ...(value === "absent" ? { check_in: null, check_out: null } : {}),
                                        };
                                        setIsApplyShift(false);
                                        setFormData(updatedFormData);
                                        if (value) {
                                            setErrors((prevState) => ({
                                                ...prevState,
                                                status: ""
                                            }));
                                        }
                                    }}
                                    filterOption={filterOption}
                                    ref={hideCursorRef}
                                    onBlur={() => hideCursorRef.current?.blur()}
                                    disabled={!isEditMode}
                                >
                                    {attendanceStatus.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.text}
                                        </Select.Option>
                                    ))}
                                </Select>
                                {errors.status && (
                                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.status}</p>
                                )}
                            </div>

                            <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Time Tracker</p>
                                {isEditMode && (
                                    <div className="flex gap-2 items-center">
                                        <Checkbox onChange={handleApplyShiftToggle} checked={isApplyShift} />
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Apply shift timing</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange("description", e.target.value)}
                                    disabled={!isEditMode}
                                    placeholder="Enter any additional notes or description..."
                                    rows={4}
                                    showCharCount={true}
                                    maxLength={250}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AttendanceEdit;